import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getPageTitle } from "../../utils/getPageTitle";
import Button from "../../components/common/button";
import "../../assets/styles/industry.css";
import "../../assets/styles/products.css";
import "../../assets/styles/pagination.css";
import IndustryModal from "../../components/modal/industryModal";
import { useViewIndustryQuery } from "../../hooks/industry/industryList";
import { useIndustryStore } from "../../hooks/industry/createIndustry";
import { useIndustryUpdate } from "../../hooks/industry/editIndustry";
import { useDeleteIndustry } from "../../hooks/industry/deleteIndustry";
import IndustryData from "../../components/industryData";
import SearchItem from "../../components/searchItem";
import Pagination from "../../components/common/pagination";
import DeleteModal from "../../components/modal/deleteModal";

const Industries = () => {
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [mode, setMode] = useState("create");
  const [localIndustries, setLocalIndustries] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalDocs: 0,
    limit: pageSize,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalKey, setModalKey] = useState(0);

  const {
    data: industriesResponse,
    isLoading,
    isFetching,
    isError,
  } = useViewIndustryQuery({
    page,
    limit: pageSize,
    q: debouncedSearchTerm,
  });
  const { mutate: addIndustry } = useIndustryStore();
  const { mutate: updateIndustry } = useIndustryUpdate();
  const { mutate: deleteIndustry } = useDeleteIndustry();

  const location = useLocation();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!industriesResponse) return;
    setLocalIndustries(industriesResponse.industries ?? []);
    const apiPagination = industriesResponse.pagination;
    if (apiPagination) {
      setPagination({
        currentPage: apiPagination.currentPage || 1,
        totalPages: apiPagination.totalPages || 1,
        totalDocs: apiPagination.total || 0,
        limit: apiPagination.limit || pageSize,
        hasNextPage: apiPagination.hasNextPage || false,
        hasPrevPage: apiPagination.hasPrevPage || false,
      });
    }
  }, [industriesResponse, pageSize]);

  const handlePageChange = (newPage) => setPage(newPage);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const handleAddIndustry = () => {
    setSelectedIndustry(null);
    setMode("create");
    setModalKey((k) => k + 1);
    setIsModalOpen(true);
  };

  const handleEdit = (industry) => {
    setSelectedIndustry(industry);
    setMode("edit");
    setModalKey((k) => k + 1);
    setIsModalOpen(true);
  };

  const handleDelete = (industry) => {
    setSelectedIndustry(industry);
    setMode("delete");
    setIsModalOpen(true);
  };

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error loading industries</div>;

  return (
    <>
      <div className="industry-page">
        <div className="industry-header">
          <div>
            <h1 className="page-title">{getPageTitle(location.pathname)}</h1>
            <p className="page-subtitle">
              Organize industries and sub-industries
            </p>
          </div>
          <Button onClick={handleAddIndustry}>+ Add Industry</Button>
        </div>

        <div className="list-toolbar">
          <div className="list-toolbar-row">
            <SearchItem
              data={localIndustries}
              searchField="title"
              placeholder="Search by industry title..."
              wrapSection={false}
              onResultsChange={({ searchItem }) => setSearchTerm(searchItem)}
            />
            <label className="list-toolbar-page-size">
              <span>Items per page:</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={20}>20</option>
              </select>
            </label>
          </div>
        </div>

        <IndustryData
          data={localIndustries}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />

        {isFetching && !isLoading && (
          <p style={{ fontSize: 12, color: "#888", marginTop: 8 }}>Updating...</p>
        )}

        <Pagination
          currentPage={page}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
        />
      </div>

      <IndustryModal
        key={modalKey}
        isOpen={isModalOpen && mode !== "delete"}
        onClose={() => setIsModalOpen(false)}
        industry={selectedIndustry}
        mode={mode}
        onSave={(formData) => {
          if (mode === "edit" && selectedIndustry) {
            updateIndustry(
              { id: selectedIndustry._id, body: formData },
              {
                onSuccess: () => {
                  setIsModalOpen(false);
                  setSelectedIndustry(null);
                },
              },
            );
          } else {
            addIndustry(formData, {
              onSuccess: () => {
                setIsModalOpen(false);
                setSelectedIndustry(null);
              },
            });
          }
        }}
        isLoading={isLoading}
      />

      <DeleteModal
        isOpen={isModalOpen && mode === "delete"}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedIndustry(null);
        }}
        onConfirm={() => {
          deleteIndustry(selectedIndustry?._id, {
            onSuccess: () => {
              setIsModalOpen(false);
              setSelectedIndustry(null);
            },
          });
        }}
        title="Delete Industry"
        itemName={selectedIndustry?.title}
        isLoading={isLoading}
      />
    </>
  );
};

export default Industries;
